import {
  Calendar,
  LucideIcon,
  PartyPopper,
  TextSearch,
  TriangleAlert,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { X } from '@/components/icons/barrel';
import { Button } from '@/components/ui';
import {
  MIGRATION_ENABLED,
  MIGRATION_NEW_APP_SIGN_IN_URL,
} from '@/config/migration';
import { ROUTES } from '@/config/routes';
import { usePrivyAccount } from '@/hooks';
import { useMigrationStore } from '@/store/use-migration-store';
import { useModalStore } from '@/store/use-modal-store';
import { MigrationState } from '@/types/migration';
import { cn } from '@/utils/cn';

type Variant = 'invite' | MigrationState;

const ICON_BY_VARIANT: Record<Variant, LucideIcon> = {
  invite: Calendar,
  pending: TextSearch,
  in_progress: TextSearch,
  blocked: TriangleAlert,
  failed: TriangleAlert,
  unknown: TriangleAlert,
  completed: PartyPopper,
};

const LINK_CLASS =
  'whitespace-nowrap text-foreground text-base underline underline-offset-2 hover:no-underline';

export default function MigrationBanner() {
  const { t } = useTranslation();
  const status = useMigrationStore((state) => state.status);
  const isAccountEmpty = useMigrationStore((state) => state.isAccountEmpty);
  const {
    address: primaryAddress,
    authenticated,
    privyReady,
  } = usePrivyAccount();
  const [dismissedKeys, setDismissedKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const isSignedOut = MIGRATION_ENABLED && privyReady && !authenticated;
  const bundle = !isSignedOut && status?.started ? status : null;

  if (status === null && !isSignedOut) return null;

  const variant: Variant = bundle ? bundle.state : 'invite';
  const isTradeNow = isSignedOut || isAccountEmpty;
  const dismissKey = `${primaryAddress?.toLowerCase() ?? ''}:${
    bundle ? `${bundle.bundleId}:${bundle.state}` : 'invite'
  }`;

  if (dismissedKeys.has(dismissKey)) return null;

  const isCompleted = variant === 'completed';
  const Icon = ICON_BY_VARIANT[variant];

  const { title, text } = ((): { title: string; text: string } => {
    switch (variant) {
      case 'invite':
        return {
          title: t('migration.banner.inviteTitle'),
          text: t(
            isTradeNow
              ? 'migration.banner.emptyText'
              : 'migration.banner.inviteText',
          ),
        };
      case 'pending':
        return {
          title: t('migration.banner.pendingTitle'),
          text: t('migration.banner.pendingText'),
        };
      case 'in_progress':
        return {
          title: t('migration.banner.movingTitle'),
          text: t('migration.banner.movingText'),
        };
      case 'blocked':
      case 'failed':
      case 'unknown':
        return {
          title: t('migration.banner.problemTitle'),
          text: t('migration.banner.problemText'),
        };
      case 'completed':
        return {
          title: t('migration.banner.completedTitle'),
          text: t('migration.banner.completedText'),
        };
    }
  })();

  const action = (() => {
    if (variant === 'invite') {
      return (
        <span className="inline-flex items-center gap-4">
          <Button
            variant="contrast"
            size="sm"
            className="h-8 px-3 text-base font-medium"
            asChild
          >
            {isTradeNow ? (
              <a
                href={MIGRATION_NEW_APP_SIGN_IN_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('migration.banner.tradeNow')}
              </a>
            ) : (
              <Link to={ROUTES.MIGRATION}>
                {t('migration.banner.inviteButton')}
              </Link>
            )}
          </Button>
          <button
            type="button"
            className={LINK_CLASS}
            onClick={() =>
              useModalStore.setState({ isMigrationWelcomeOpen: true })
            }
          >
            {t('migration.banner.inviteLink')}
          </button>
        </span>
      );
    }
    return (
      <Link to={ROUTES.MIGRATION} className={LINK_CLASS}>
        {t(
          isCompleted
            ? 'migration.banner.seeDetails'
            : 'migration.banner.checkProgress',
        )}
      </Link>
    );
  })();

  return (
    <div
      role="status"
      className={cn(
        'relative z-50 flex items-center justify-center px-12 py-2.5 sm:px-16',
        isCompleted ? 'bg-green/10' : 'bg-yellow/10',
      )}
    >
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-center text-[13px] leading-snug">
        <Icon
          size={18}
          className={cn('shrink-0', isCompleted ? 'text-green' : 'text-yellow')}
          aria-hidden="true"
        />
        <p className="mr-2">
          <strong className="font-medium">{title}</strong>
          <span className="hidden text-muted-foreground md:inline">
            {' '}
            {text}
          </span>
        </p>
        {action}
      </div>
      <button
        type="button"
        aria-label={t('migration.banner.dismiss')}
        className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-muted-foreground transition-colors hover:text-foreground sm:right-6"
        onClick={() =>
          setDismissedKeys((keys) => new Set(keys).add(dismissKey))
        }
      >
        <X size={16} />
      </button>
    </div>
  );
}
